// src/components/modals/CreateRecipeModal.jsx
import { useState, useEffect, useMemo } from 'react'
import { X, Plus, Trash2, ChevronDown } from 'lucide-react'
import PropTypes from 'prop-types'
import SearchableSelect from '../common/SearchableSelect'

const CreateRecipeModal = ({ isOpen, onClose, onSubmit, products, rawMaterials }) => {
  const [formData, setFormData] = useState({
    name: '',
    cost: '',
    product: '',
    rawMaterialList: []
  })

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [selectedRawMaterial, setSelectedRawMaterial] = useState('')

  // Map product options for SearchableSelect
  const productOptions = useMemo(() => {
    return (products || []).map(product => ({
      value: product._id,
      label: product.name,
      subtext: product.sku ? `SKU: ${product.sku}` : undefined
    }))
  }, [products])

  // Map raw material options for SearchableSelect
  const rawMaterialOptions = useMemo(() => {
    return (rawMaterials || [])
      .filter(material => !formData.rawMaterialList.some(item => item.rawMaterial === material._id))
      .map(material => ({
        value: material._id,
        label: material.name,
        subtext: `Unit: ${material.unit?.abbreviation || material.unit?.name || 'unit'}`
      }))
  }, [rawMaterials, formData.rawMaterialList])

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        cost: '',
        product: '',
        rawMaterialList: []
      })
      setErrors({})
      setSelectedRawMaterial('')
    }
  }, [isOpen])

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Add raw material to list
  const addRawMaterial = () => {
    if (!selectedRawMaterial) {
      setErrors(prev => ({
        ...prev,
        rawMaterial: 'Please select a raw material'
      }))
      return
    }

    // Check if material already exists
    const exists = formData.rawMaterialList.some(item => item.rawMaterial === selectedRawMaterial)
    if (exists) {
      setErrors(prev => ({
        ...prev,
        rawMaterial: 'This raw material is already added'
      }))
      return
    }

    const material = rawMaterials.find(rm => rm._id === selectedRawMaterial)
    if (!material) {
      setErrors(prev => ({
        ...prev,
        rawMaterial: 'Invalid raw material selected'
      }))
      return
    }

    const newItem = {
      rawMaterial: selectedRawMaterial,
      quantity: '', // Start with empty string instead of 0
      materialData: material // Store for display purposes
    }

    setFormData(prev => ({
      ...prev,
      rawMaterialList: [...prev.rawMaterialList, newItem]
    }))

    setSelectedRawMaterial('')
    setErrors(prev => ({
      ...prev,
      rawMaterial: ''
    }))
  }

  // Remove raw material from list
  const removeRawMaterial = (index) => {
    setFormData(prev => ({
      ...prev,
      rawMaterialList: prev.rawMaterialList.filter((_, i) => i !== index)
    }))
  }

  // Update raw material quantity
  const updateRawMaterialQuantity = (index, quantity) => {
    setFormData(prev => ({
      ...prev,
      rawMaterialList: prev.rawMaterialList.map((item, i) =>
        i === index ? { ...item, quantity: quantity } : item
      )
    }))
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Recipe name is required'
    }

    if (!formData.product) {
      newErrors.product = 'Product selection is required'
    }

    if (!formData.cost || isNaN(formData.cost) || parseFloat(formData.cost) < 0) {
      newErrors.cost = 'Valid cost is required'
    }

    if (formData.rawMaterialList.length === 0) {
      newErrors.rawMaterialList = 'At least one raw material is required'
    }

    // Validate raw material quantities
    const invalidQuantities = formData.rawMaterialList.some(item =>
      !item.quantity || item.quantity === '' || isNaN(item.quantity) || parseFloat(item.quantity) <= 0
    )

    if (invalidQuantities) {
      newErrors.rawMaterialList = 'All raw materials must have valid quantities greater than 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      // Prepare data for API
      const submitData = {
        name: formData.name.trim(),
        cost: parseFloat(formData.cost),
        product: formData.product,
        rawMaterialList: formData.rawMaterialList.map(item => ({
          rawMaterial: item.rawMaterial,
          quantity: parseFloat(item.quantity) || 0
        }))
      }

      await onSubmit(submitData)
    } catch (error) {
      console.error('Error submitting recipe:', error)
      setErrors({ submit: error.message || 'Failed to create recipe' })
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Create New Recipe</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Recipe Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipe Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="eg.Espresso Standard"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              disabled={loading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Cost and Product */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Cost */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cost *
              </label>
              <input
                type="number"
                name="cost"
                value={formData.cost}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                disabled={loading}
              />
              {errors.cost && (
                <p className="mt-1 text-sm text-red-600">{errors.cost}</p>
              )}
            </div>

            {/* Product */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product name *
              </label>
              <SearchableSelect
                options={productOptions}
                value={formData.product}
                onChange={(val) => {
                  handleInputChange({ target: { name: 'product', value: val } })
                }}
                placeholder="Select Product"
                searchPlaceholder="Search product name..."
                disabled={loading}
                error={!!errors.product}
              />
              {errors.product && (
                <p className="mt-1 text-sm text-red-600">{errors.product}</p>
              )}
            </div>
          </div>

          {/* Raw Material List */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Raw Material List *
            </label>

            {/* Add Raw Material Section */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex gap-2 mb-2">
                <div className="flex-1">
                  <SearchableSelect
                    options={rawMaterialOptions}
                    value={selectedRawMaterial}
                    onChange={(val) => {
                      setSelectedRawMaterial(val)
                      if (errors.rawMaterial) {
                        setErrors(prev => ({ ...prev, rawMaterial: '' }))
                      }
                    }}
                    placeholder="Select Raw Material"
                    searchPlaceholder="Search raw material name..."
                    disabled={loading}
                    error={!!errors.rawMaterial}
                  />
                </div>
                <button
                  type="button"
                  onClick={addRawMaterial}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                  disabled={loading}
                >
                  <Plus size={16} />
                  Add
                </button>
              </div>
              {errors.rawMaterial && (
                <p className="text-sm text-red-600">{errors.rawMaterial}</p>
              )}
            </div>

            {/* Raw Material List Display */}
            {formData.rawMaterialList.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-sm font-medium text-gray-700 px-2">
                  <div className="col-span-4">Raw material</div>
                  <div className="col-span-2">Unit</div>
                  <div className="col-span-4">Qty usage</div>
                  <div className="col-span-2">Actions</div>
                </div>

                {formData.rawMaterialList.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center p-2 bg-gray-50 rounded">
                    <div className="col-span-4 text-sm">
                      {item.materialData?.name || 'Unknown Material'}
                    </div>
                    <div className="col-span-2 text-sm text-gray-600">
                      {item.materialData?.unit?.abbreviation || item.materialData?.unit?.name || item.materialData?.unit || 'N/A'}
                    </div>
                    <div className="col-span-4">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateRawMaterialQuantity(index, e.target.value)}
                        placeholder="Enter quantity (e.g. 0.0050)"
                        step="0.0001"
                        min="0"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
                        disabled={loading}
                      />
                    </div>
                    <div className="col-span-2">
                      <button
                        type="button"
                        onClick={() => removeRawMaterial(index)}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                        disabled={loading}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {errors.rawMaterialList && (
              <p className="mt-2 text-sm text-red-600">{errors.rawMaterialList}</p>
            )}
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Recipe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

CreateRecipeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  products: PropTypes.array.isRequired,
  rawMaterials: PropTypes.array.isRequired
}

export default CreateRecipeModal